import { Distributions } from "@custom-types/graphs"
import { Validate } from "@features/builder/graph/nodes/validation/pure/getDfCpdValidationState/validatoins"
import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import { DfCpd } from "@store/services/converter/types"
import { DfCpdStrictRow, dfCpdToStrictRows } from "@utils/node/dfCpd"

type DfCpdValidationContext = {
  /**
   * Whether the node owning this df_cpd has at least one child.
   *
   * Geometric and Logseries require `ubound` only in that case — see
   * `Validate.uboundRequiredWithChildren`. Mandatory on purpose: a default would let
   * a caller silently validate a parent as a leaf and miss the error.
   */
  hasChildren: boolean
}

export function getDfCpdValidation(
  dfCpd: DfCpd,
  { hasChildren }: DfCpdValidationContext,
): ValidationState {
  const state: ValidationState = { status: "valid", errors: [] }

  const rows = dfCpdToStrictRows(dfCpd)

  for (const [idx, row] of rows.entries()) {
    const distribution = row.distribution
    if (!distribution || distribution === "Categorical") continue

    if (!(distribution in validateByDistribution)) continue

    const getErrors = validateByDistribution[distribution as keyof typeof validateByDistribution]
    if (!getErrors) continue

    state.errors.push(...getErrors(idx, row, hasChildren))
  }

  state.status = state.errors.length ? "error" : "valid"
  return state
}

const validateByDistribution: Omit<
  Record<
    Distributions,
    | ((idx: number, row: DfCpdStrictRow, hasChildren: boolean) => ValidationState["errors"])
    | undefined
  >,
  "Categorical" // validated separately
> = {
  //--------------------
  // Common Loc and Scale
  //--------------------
  Normal: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Cauchy: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Exponential: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Laplace: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Logistic: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Gumbel: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Uniform: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Rayleigh: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  Wald: (idx: number, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
    ].filter(Boolean),

  //--------------------
  // Uncommon
  //--------------------
  Beta: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "a", "b"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.a(idx, row),
      ...Validate.b(idx, row),
    ].filter(Boolean),

  Binomial: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "n", "p"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.n(idx, row),
      ...Validate.p(idx, row),
    ].filter(Boolean),

  Chisquare: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "df"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.df(idx, row),
    ].filter(Boolean),

  F: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "dfn", "dfd"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.dfn(idx, row),
      ...Validate.dfd(idx, row),
    ].filter(Boolean),

  Gamma: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "a"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.a(idx, row),
    ].filter(Boolean),

  Geometric: (idx, row, hasChildren) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "p"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.uboundRequiredWithChildren(idx, row, hasChildren),
      ...Validate.nonEmptyStateSpace(idx, row),
      ...Validate.uboundWithinLimit(idx, row),
      ...Validate.pAllowOne(idx, row),
    ].filter(Boolean),

  Hypergeometric: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "M", "n", "N"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.M(idx, row),
      ...Validate.nHypergeometric(idx, row),
      ...Validate.N(idx, row),
    ].filter(Boolean),

  Lognormal: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "s"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.s(idx, row),
    ].filter(Boolean),

  Logseries: (idx, row, hasChildren) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "p"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.uboundRequiredWithChildren(idx, row, hasChildren),
      ...Validate.nonEmptyStateSpace(idx, row),
      ...Validate.uboundWithinLimit(idx, row),
      ...Validate.p(idx, row),
    ].filter(Boolean),

  NonCentralChisquare: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "df", "nc"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.df(idx, row),
      ...Validate.nc(idx, row),
    ].filter(Boolean),

  NonCentralF: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "dfn", "dfd", "nc"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.dfn(idx, row),
      ...Validate.dfd(idx, row),
      ...Validate.nc(idx, row),
    ].filter(Boolean),

  Pareto: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "b"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.b(idx, row),
    ].filter(Boolean),

  Poisson: (idx, row, hasChildren) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "mu"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.uboundRequiredWithChildren(idx, row, hasChildren),
      ...Validate.nonEmptyStateSpace(idx, row),
      ...Validate.uboundWithinLimit(idx, row),
      ...Validate.mu(idx, row),
    ].filter(Boolean),

  Power: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "a"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.a(idx, row),
    ].filter(Boolean),

  T: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "df"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.df(idx, row),
    ].filter(Boolean),

  Triangular: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "c"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.c(idx, row),
    ].filter(Boolean),

  Vonmises: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "kappa"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.kappa(idx, row),
    ].filter(Boolean),

  Weibull: (idx, row) =>
    [
      //
      ...Validate.mandatoryParams(idx, row, ["loc", "scale", "c"]),
      ...Validate.bounds(idx, row),
      ...Validate.loc(idx, row),
      ...Validate.scale(idx, row),
      ...Validate.cPositive(idx, row),
    ].filter(Boolean),
}
